import React, { useState, useMemo } from 'react';
import '../styles/Header.css';
import logo from '../logo.svg';
import logoMark from '../logo-mark.svg';
import { useNavigate } from 'react-router-dom';
import { cloudFunctionV2, logout, recordEvent } from '../functions';
import DefaultUserImage from './DefaultUserImage';
import ExitScreen from './ExitScreen';
import Spinner from './Spinner';
import { HugeiconsIcon } from '@hugeicons/react';
import { Logout05Icon, Plug02Icon, Settings01Icon, UserCircleIcon, Coins01Icon, CreditCardPosIcon } from '@hugeicons/core-free-icons';
import { colour, numberFormatter } from '../styles/GlobalStyles';
import { useUser } from '../contexts';
import { useCompany } from '../contexts';
import { isBlockedUserId } from '../utils/userFlags';

function Header(props) {
    const showTimeline = props.showTimeline === undefined ? false : props.showTimeline;
    const navigate = useNavigate();
    const [openPanel, setOpenPanel] = useState(false);
    const [stripeLoading, setStripeLoading] = useState(false);

    // Get data from context (hooks must be called unconditionally)
    const userContext = useUser();
    const companyContext = useCompany();

    // Use loggedIn from context to ensure it updates immediately when auth state changes
    const loggedIn = userContext.loggedIn;
    const companyName = companyContext.companyName || "";
    const companyLogo = companyContext.companyLogo || null;
    const credits = companyContext.credits ?? null;
    const customerId = companyContext.customerId || null;
    const planName = companyContext.companyData?.plan?.name || null;

    const isBlockedUser = isBlockedUserId(userContext?.user?.userId);

    const login = () => {
        navigate("/login");
    };

    const togglePanel = () => {
        setOpenPanel(!openPanel);
        recordEvent("toggle_header_panel", {});
    };

    const getStripePortal = async() => {
        if (stripeLoading) return;
        const url = process.env.REACT_APP_STRIPE_PORTAL;
        if (!url) {
            alert('Billing portal is not configured. Please contact support.');
            return;
        }
        setStripeLoading(true);
        try {
            const firebaseUser = userContext.user?.user;
            const idToken = firebaseUser && typeof firebaseUser.getIdToken === 'function'
                ? await firebaseUser.getIdToken(true)
                : null;
            if (!idToken) {
                alert('Unable to authenticate. Please refresh and try again.');
                return;
            }
            const queryData = await cloudFunctionV2(url, { idToken, returnURL: window.location.href });
            if ( queryData.status === 200 ) {
                recordEvent("open_stripe_portal", {});
                window.open(queryData.url, "_self");
            } else {
                alert('Unable to open billing portal. Please try again or contact support.');
            }
        } catch (err) {
            console.error('Stripe portal error:', err);
            alert('An unexpected error occurred. Please try again.');
        } finally {
            setStripeLoading(false);
        }
    };

    const popoverBody = useMemo(() => [
        {
            label: "Integrations",
            icon: <HugeiconsIcon icon={Plug02Icon} color={colour.gray06} size={14} />,
            cta: () => "/integrations",
            external: false,
            subscriptionManager: false
        },
        {
            label: "Settings",
            icon: <HugeiconsIcon icon={Settings01Icon} color={colour.gray06} size={14} />,
            cta: () => "/settings",
            external: false,
            subscriptionManager: false
        },
        ...(customerId ? [{
            label: "Manage subscription",
            icon: <HugeiconsIcon icon={CreditCardPosIcon} color={colour.gray06} size={14} />,
            cta: () => getStripePortal(),
            external: false,
            subscriptionManager: true
        }] : []),
        {
            label: "Profile",
            icon: <HugeiconsIcon icon={UserCircleIcon} color={colour.gray06} size={14} />,
            cta: () => "/profile",
            external: false,
            subscriptionManager: false
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    ], [customerId]);

    const bookDemo = () => {
        recordEvent("book_demo", {});
        window.open("https://calendly.com/liam-maher/clozers-demo-form", "_blank");
    };

    const logoutFunc = async() => {
        recordEvent("logout", {});
        await logout();
        navigate("/login");
    };

    return (
        <React.Fragment>
            {
                openPanel === true &&
                <ExitScreen
                    clickScreen={() => setOpenPanel(false)}
                />
            }
            <div className={"header-outer-container " + (showTimeline === true ? "header-agentic-timeline" : "")}>
                <div className="header-inner-container">
                    <a href="/" className="">
                        <img
                            src={logo}
                            className="header-logo header-logo-full cursor-pointer"
                            alt="Logo"
                        />
                        <img
                            src={logoMark}
                            className="header-logo header-logo-mark cursor-pointer"
                            alt="Logo"
                        />
                    </a>
                    {
                        loggedIn === false ?
                        <div 
                            className="text-button"
                            onClick={() => login()}
                        >
                            <span className="body-regular colour-primary">
                                Login
                            </span>
                        </div>
                        :
                        loggedIn !== null &&
                        <div className="header-auth-container relative-container">
                            <div 
                                className="text-button"
                                onClick={() => bookDemo()}
                            >
                                <span className="body-regular colour-primary">
                                    Book a demo
                                </span>
                            </div>
                            <DefaultUserImage
                                size="large"
                                clickFunc={() => loggedIn === true ? togglePanel() : login()}
                                image={companyLogo}
                            />
                            <div className={"header-popover-container popover " + (openPanel === true ? "popover-visible" : "")}>
                                    <div className="header-popover-element">
                                        <DefaultUserImage
                                            size="large"
                                            clickFunc={null}
                                            image={companyLogo}
                                        />
                                        <div className="header-popover-company-name-container">
                                            <span className="body-semibold colour-primary">
                                                {companyName}
                                            </span>
                                            {planName && (
                                                <span className="xs-label-bold colour-secondary">
                                                    {planName} plan
                                                </span>
                                            )}
                                            <div className="header-popover-company-credits-container">
                                                <HugeiconsIcon
                                                    icon={Coins01Icon}
                                                    color={colour.gray05}
                                                    size={16}
                                                />
                                                <span className="body-regular colour-secondary">
                                                    {numberFormatter.format(credits ?? 0)} credits remaining
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="header-popover-body-container">
                                        {
                                            popoverBody.map((item, index) =>
                                                item.subscriptionManager === true ?
                                                <div
                                                    className={"header-popover-body-element no-underline " + (stripeLoading ? "cursor-default" : "cursor-pointer")}
                                                    key={index}
                                                    onClick={() => getStripePortal()}
                                                >
                                                    {stripeLoading ? <Spinner className="spinner-xs" /> : item.icon}
                                                    <span className="body-regular colour-primary">
                                                        {item.label}
                                                    </span>
                                                </div>
                                                :
                                                <a 
                                                    className="header-popover-body-element cursor-pointer no-underline"
                                                    key={index}
                                                    target={item.external === true ? "_blank" : "_self"}
                                                    href={item.cta()}
                                                    rel={item.external === true ? "noreferrer" : undefined}
                                                >
                                                    {item.icon}
                                                    <span className="body-regular colour-primary">
                                                        {item.label}
                                                    </span>
                                                </a>
                                            )
                                        }
                                    </div>
                                    {
                                        !isBlockedUser && (
                                            <div 
                                                className="header-popover-body-element cursor-pointer"
                                                onClick={() => logoutFunc()}
                                            >
                                                <HugeiconsIcon
                                                    icon={Logout05Icon}
                                                    color={colour.gray05}
                                                    size={14}
                                                />
                                                <span className="body-regular colour-secondary">
                                                    Logout
                                                </span>
                                            </div>
                                        )
                                    }
                                </div>
                        </div>
                    }
                </div>
            </div>
        </React.Fragment>
    )
};

export default Header;